import dayjs from 'dayjs'

const FILE_HOST = process.env.REACT_APP_AWS_MEDIA_URL;

export function cloudfrontUrl(value) {
    if (!value) return ''
    const text = String(value)
    if (text.includes('amazonaws.com/')) {
        const key = text.split('amazonaws.com/')[1] || ''
        return `${FILE_HOST}${key.replace(/^\/+/, '')}`
    }
    if (text.startsWith('http')) return text
    return `${FILE_HOST}${text.replace(/^\/+/, '')}`
}

export function fileUrl(item) {
    if (!item) return ''
    return cloudfrontUrl(item.fileName || item.fileUrl)
}

export function formatDate(value) {
    return value ? dayjs(value).format('MM-DD-YYYY') : '—'
}

export function displayEmployeeCode(item) {
    const person = item?.Employee || {}
    const status = item?.employmentStatus || 'active'
    if (status === 'terminated' && person._id && item?.Company?.name) {
        return `${item.Company.name.substring(0, 3)}${String(person._id).slice(-3)}`
    }
    return person.employeeId || ''
}

export function progressLabel(status) {
    if (status === 'in_progress') return 'In progress'
    if (status === 'completed') return 'Completed'
    if (status === 'expired') return 'Expired'
    return 'Assigned'
}

export function progressClass(status) {
    if (status === 'completed') return 'bg-green-100 text-green-800 border-green-200'
    if (status === 'in_progress') return 'bg-blue-100 text-blue-800 border-blue-200'
    if (status === 'expired') return 'bg-red-100 text-red-800 border-red-200'
    return 'bg-slate-100 text-slate-700 border-slate-200'
}

export function materialSummary(row) {
    const materials = Array.isArray(row?.materials) ? row.materials : []
    const total = materials.length || (row?.materialType ? 1 : 0)
    const done = materials.length
        ? materials.filter((material) => material.completed).length
        : (row?.status === 'completed' && row?.materialType ? 1 : 0)
    const videos = materials.filter((material) => material.kind === 'video')
    let percent = null
    if (videos.length) {
        percent = Math.round(videos.reduce((sum, material) => {
            if (material.completed) return sum + 100
            if (material.videoDuration) {
                return sum + Math.min(100, Math.round((Number(material.watchedSeconds) / material.videoDuration) * 100))
            }
            return sum + (Number(material.progressPercent) || 0)
        }, 0) / videos.length)
    } else if (row?.materialType === 'video') {
        if (row.status === 'completed') percent = 100
        else if (row.videoDuration) percent = Math.min(100, Math.round((Number(row.watchedSeconds) / row.videoDuration) * 100))
        else percent = Number(row.progressPercent) || 0
    }
    return { total, done, percent }
}

export function isImageFile(item) {
    const type = String(item?.fileType || '')
    const name = String(item?.originalName || item?.fileName || '')
    return type.startsWith('image/') || /\.(jpe?g|png|gif|webp)$/i.test(name)
}

export function groupCertificates(items) {
    const grouped = new Map()
    items.forEach((item) => {
        const key = String(item.userId || item.employeeCode || item.employeeName || item._id)
        if (!grouped.has(key)) {
            grouped.set(key, {
                key,
                employeeName: item.employeeName || '',
                employeeCode: item.employeeCode || '',
                jobTitle: item.jobTitle || '',
                documents: []
            })
        }
        const group = grouped.get(key)
        if (!group.jobTitle && item.jobTitle) group.jobTitle = item.jobTitle
        group.documents.push(item)
    })
    return [...grouped.values()]
}

export function verificationSummary(documents) {
    const counts = documents.reduce((total, item) => {
        const key = item.verification || 'pending'
        total[key] = (total[key] || 0) + 1
        return total
    }, {})
    return ['pending', 'verified', 'rejected']
        .filter((key) => counts[key])
        .map((key) => `${counts[key]} ${key}`)
        .join(', ')
}

export function expirySummary(documents) {
    const labels = documents.map((item) => item.expiryTracking || 'No expiry')
    if (labels.includes('Expired')) return 'Expired'
    if (labels.includes('Expiring')) return 'Expiring'
    if (labels.includes('Valid')) return 'Valid'
    return 'No expiry'
}

export const badgeBase = 'inline-block px-2.5 py-0.5 text-xs font-medium rounded-full border whitespace-nowrap'

export function countByStatus(assignments = []) {
    const counts = { assigned: 0, in_progress: 0, completed: 0 }
    assignments.forEach((row) => {
        const key = row.status === 'completed' || row.status === 'in_progress' ? row.status : 'assigned'
        counts[key] += 1
    })
    return counts
}

export function materialKindLabel(kind) {
    if (kind === 'link') return 'URL'
    if (kind === 'video') return 'Video'
    if (kind === 'pdf') return 'Docs'
    return kind || '—'
}

// e.g. "Video ×2, URL"
export function materialKindsLabel(row) {
    const materials = Array.isArray(row?.materials) ? row.materials : []
    if (!materials.length) return row?.materialType ? materialKindLabel(row.materialType) : '—'
    const counts = {}
    materials.forEach((material) => {
        const label = materialKindLabel(material.kind)
        counts[label] = (counts[label] || 0) + 1
    })
    return Object.entries(counts)
        .map(([label, count]) => (count > 1 ? `${label} ×${count}` : label))
        .join(', ')
}